package top.wcpe.allincore.ui.web.asset

import kotlin.js.Promise

internal class HttpAssetSourceAdapter(
    override val source: AssetSource,
    baseUri: String,
    private val uriBackend: AssetUriBackend,
    private val fetchBackend: AssetFetchBackend,
) : AssetSourceAdapter {
    private val base = requireHttpUri(uriBackend.parse(baseUri.withDirectorySlash()), "HTTP 资产 base")
    private val active = mutableSetOf<AssetFetchOperation>()
    private var disposed = false

    init {
        if (source !in setOf(AssetSource.URL, AssetSource.PAGES)) {
            throw AssetPolicyException("HTTP adapter 仅支持 URL 或 Pages 来源")
        }
    }

    override fun read(canonicalPath: String, readLimit: AssetReadLimit): AssetSourceReadOperation {
        if (disposed) return rejectedSourceRead("HTTP 资产来源已释放")
        val target = resolveTarget(canonicalPath) ?: return rejectedSourceRead("HTTP 资产 base 无效")
        val fetch = fetchBackend.fetch(target.value, readLimit)
        val state = CancellationState()
        active += fetch
        val result = fetch.result.then(
            onFulfilled = { response -> completeRead(fetch, state, response, readLimit) },
            onRejected = { failRead(fetch, state, AssetSourceFailure.READ_FAILED, "HTTP 资产读取失败") },
        )
        return AssetSourceReadOperation(result) {
            state.cancelled = true
            fetch.cancel()
        }
    }

    override fun dispose() {
        if (disposed) return
        disposed = true
        active.toList().forEach(AssetFetchOperation::cancel)
        active.clear()
    }

    private fun resolveTarget(canonicalPath: String): AssetUri? = try {
        uriBackend.resolve(base.value, canonicalPath).takeIf { candidate ->
            candidate.isNonOpaqueHttp() && contained(candidate)
        }
    } catch (ignored: Throwable) {
        null
    }

    private fun completeRead(
        fetch: AssetFetchOperation,
        state: CancellationState,
        response: AssetFetchResponse,
        readLimit: AssetReadLimit,
    ): AssetSourceRead {
        active -= fetch
        if (disposed) failSource("HTTP 资产来源已释放")
        if (state.cancelled) failSource("HTTP 资产读取已取消")
        validateResponse(response, readLimit)
        return AssetSourceRead(
            response.bytes,
            response.declaredMime,
            response.etag ?: response.lastModified,
            response.contentLength,
        )
    }

    private fun failRead(
        fetch: AssetFetchOperation,
        state: CancellationState,
        failure: AssetSourceFailure,
        message: String,
    ): Nothing {
        active -= fetch
        if (disposed) failSource("HTTP 资产来源已释放")
        if (state.cancelled) failSource("HTTP 资产读取已取消")
        failSource(failure, message)
    }

    private fun validateResponse(response: AssetFetchResponse, readLimit: AssetReadLimit) {
        if (response.status == HTTP_NOT_FOUND) failSource(AssetSourceFailure.NOT_FOUND, "HTTP 资产不存在")
        if (response.status !in 200..299) failSource(AssetSourceFailure.READ_FAILED, "HTTP 状态不是成功状态")
        val finalUri = parseFinal(response.finalUrl)
        requireHttpUri(finalUri, "HTTP 资产最终响应")
        if (finalUri.search.isNotEmpty() || finalUri.hash.isNotEmpty()) {
            failSource("HTTP 资产最终响应不得包含查询参数或片段")
        }
        if (!contained(finalUri)) failSource("HTTP 资产重定向越出受控 base")
        if (response.declaredMime.isNullOrBlank()) failSource("HTTP 资产响应 MIME 不能为空")
        val classifiedLimit = readLimit.byteLimitFor(response.bytes)
        if (response.contentLength != null && response.contentLength > classifiedLimit) {
            failSource("HTTP 资产 Content-Length 超过预算")
        }
        if (response.bytes.size.toLong() > classifiedLimit) failSource("HTTP 资产实际字节超过预算")
    }

    private fun parseFinal(uri: String): AssetUri = try {
        uriBackend.parse(uri)
    } catch (ignored: Throwable) {
        failSource("HTTP 资产最终响应地址无效")
    }

    private fun contained(candidate: AssetUri): Boolean = AssetContainmentPolicy.contains(
        base.origin,
        base.pathname,
        candidate.origin,
        candidate.pathname,
    )
}

internal class FileSystemAssetSourceAdapter(
    root: AssetFileSystemDirectoryHandle,
    knownFiles: Map<String, AssetFileSystemFileHandle> = emptyMap(),
    private val restrictToKnownFiles: Boolean = false,
    private val pathAllowed: (String) -> Boolean = { true },
) : AssetSourceAdapter {
    override val source: AssetSource = AssetSource.FILE_SYSTEM
    private var rootHandle: AssetFileSystemDirectoryHandle? = root
    private val fileHandles = knownFiles.toMutableMap()
    private val active = mutableSetOf<CancellationState>()

    override fun read(canonicalPath: String, readLimit: AssetReadLimit): AssetSourceReadOperation {
        val root = rootHandle ?: return rejectedSourceRead("文件系统资产来源已释放")
        val state = CancellationState()
        active += state
        val segments = canonicalPath.split('/')
        val result = Promise<AssetSourceRead> { resolve, reject ->
            state.reject = reject
            resolveFile(root, segments, state).then(
                onFulfilled = { file -> inspectFile(file, state, readLimit, resolve, reject) },
                onRejected = { reason -> rejectFileRead(state, reason, reject) },
            )
        }
        return AssetSourceReadOperation(result) { cancelFileRead(state) }
    }

    override fun dispose() {
        if (rootHandle == null) return
        rootHandle = null
        fileHandles.clear()
        active.toList().forEach { state ->
            settleFileRead(state, AssetPolicyException("文件系统资产来源已释放"), state.reject)
        }
    }

    internal fun activeReadCount(): Int = active.size

    private fun cancelFileRead(state: CancellationState) {
        if (state.settled) return
        state.cancelled = true
        settleFileRead(state, AssetPolicyException("文件系统资产读取已取消"), state.reject)
    }

    private fun settleFileRead(state: CancellationState, error: Throwable, reject: ((Throwable) -> Unit)?) {
        if (state.settled) return
        state.settled = true
        active -= state
        state.reject = null
        reject?.invoke(error)
    }

    private fun resolveFile(
        root: AssetFileSystemDirectoryHandle,
        segments: List<String>,
        state: CancellationState,
    ): Promise<AssetFileSystemFileHandle> {
        val path = segments.joinToString("/")
        if (!pathAllowed(path)) return Promise.reject(AssetFileSystemException(AssetFileSystemFailure.NOT_FOUND))
        fileHandles[path]?.let { return Promise.resolve(it) }
        if (restrictToKnownFiles) {
            return Promise.reject(AssetFileSystemException(AssetFileSystemFailure.NOT_FOUND))
        }
        return Promise { resolve, reject ->
            continueFileSystem(state, reject) {
                resolveDirectory(root, segments.dropLast(1), 0, cacheFile(path, resolve), reject, segments.last(), state)
            }
        }
    }

    private fun cacheFile(
        path: String,
        resolve: (AssetFileSystemFileHandle) -> Unit,
    ): (AssetFileSystemFileHandle) -> Unit = { file ->
        fileHandles[path] = file
        resolve(file)
    }

    private fun resolveDirectory(
        directory: AssetFileSystemDirectoryHandle,
        segments: List<String>,
        index: Int,
        resolve: (AssetFileSystemFileHandle) -> Unit,
        reject: (Throwable) -> Unit,
        fileName: String,
        state: CancellationState,
    ) {
        if (index == segments.size) {
            requestFileHandle(directory, fileName, state, resolve, reject)
            return
        }
        continueFileSystem(state, reject) {
            directory.getDirectoryHandle(segments[index]).then(
                onFulfilled = { child ->
                    continueFileSystem(state, reject) {
                        resolveDirectory(child, segments, index + 1, resolve, reject, fileName, state)
                    }
                },
                onRejected = reject,
            )
        }
    }

    private fun requestFileHandle(
        directory: AssetFileSystemDirectoryHandle,
        fileName: String,
        state: CancellationState,
        resolve: (AssetFileSystemFileHandle) -> Unit,
        reject: (Throwable) -> Unit,
    ) {
        continueFileSystem(state, reject) {
            directory.getFileHandle(fileName).then(
                onFulfilled = { file -> continueFileSystem(state, reject) { resolve(file) } },
                onRejected = reject,
            )
        }
    }

    private fun inspectFile(
        file: AssetFileSystemFileHandle,
        state: CancellationState,
        readLimit: AssetReadLimit,
        resolve: (AssetSourceRead) -> Unit,
        reject: (Throwable) -> Unit,
    ) {
        continueFileSystem(state, reject) {
            file.openSnapshot(AICR_MAGIC_BYTES).then(
                onFulfilled = { snapshot ->
                    continueSnapshotRead(snapshot, state, readLimit, resolve, reject)
                },
                onRejected = { reason -> rejectFileRead(state, reason, reject) },
            )
        }
    }

    private fun continueSnapshotRead(
        snapshot: AssetFileSystemSnapshot,
        state: CancellationState,
        readLimit: AssetReadLimit,
        resolve: (AssetSourceRead) -> Unit,
        reject: (Throwable) -> Unit,
    ) {
        continueFileSystem(state, reject) {
            val classifiedLimit = classifiedReadLimit(snapshot, readLimit)
            snapshot.read(classifiedLimit).then(
                onFulfilled = { bytes ->
                    completeSnapshot(state, snapshot, bytes, readLimit, classifiedLimit, resolve, reject)
                },
                onRejected = { reason -> rejectFileRead(state, reason, reject) },
            )
        }
    }

    private fun completeSnapshot(
        state: CancellationState,
        snapshot: AssetFileSystemSnapshot,
        bytes: ByteArray,
        readLimit: AssetReadLimit,
        classifiedLimit: Long,
        resolve: (AssetSourceRead) -> Unit,
        reject: (Throwable) -> Unit,
    ) {
        if (state.settled) return
        try {
            val read = completeFileRead(state, snapshot, bytes, readLimit, classifiedLimit)
            state.settled = true
            state.reject = null
            resolve(read)
        } catch (error: Throwable) {
            settleFileRead(state, error, reject)
        }
    }

    private fun classifiedReadLimit(snapshot: AssetFileSystemSnapshot, readLimit: AssetReadLimit): Long {
        val expectedPrefixBytes = minOf(snapshot.size, AICR_MAGIC_BYTES.toLong()).toInt()
        if (snapshot.size < 0L || snapshot.prefix.size != expectedPrefixBytes) {
            failSource("文件系统资产 snapshot 结果无效")
        }
        val classifiedLimit = readLimit.byteLimitFor(snapshot.prefix)
        if (snapshot.size > classifiedLimit) failSource("文件系统资产超过预算")
        return classifiedLimit
    }

    private fun continueFileSystem(state: CancellationState, reject: (Throwable) -> Unit, action: () -> Unit) {
        if (state.settled) return
        try {
            ensureFileSystemActive(state)
            action()
        } catch (error: Throwable) {
            active -= state
            reject(error)
        }
    }

    private fun ensureFileSystemActive(state: CancellationState) {
        if (rootHandle == null) failSource("文件系统资产来源已释放")
        if (state.cancelled) failSource("文件系统资产读取已取消")
    }

    private fun completeFileRead(
        state: CancellationState,
        snapshot: AssetFileSystemSnapshot,
        bytes: ByteArray,
        readLimit: AssetReadLimit,
        classifiedLimit: Long,
    ): AssetSourceRead {
        active -= state
        ensureFileSystemActive(state)
        if (bytes.size.toLong() != snapshot.size) failSource("文件系统资产未完整读取")
        val actualLimit = readLimit.byteLimitFor(bytes)
        val effectiveLimit = minOf(classifiedLimit, actualLimit)
        if (snapshot.size > effectiveLimit || bytes.size.toLong() > effectiveLimit) {
            failSource("文件系统资产超过预算")
        }
        return AssetSourceRead(
            bytes,
            snapshot.declaredMime,
            "lastModified:${snapshot.lastModified}:size:${snapshot.size}",
            snapshot.size,
        )
    }

    private fun rejectFileRead(state: CancellationState, reason: Throwable, reject: (Throwable) -> Unit) {
        if (state.settled) return
        try {
            failFileRead(state, reason)
        } catch (error: Throwable) {
            settleFileRead(state, error, reject)
        }
    }

    private fun failFileRead(state: CancellationState, reason: Throwable): Nothing {
        active -= state
        if (rootHandle == null) failSource("文件系统资产来源已释放")
        if (state.cancelled) failSource("文件系统资产读取已取消")
        val failure = (reason as? AssetFileSystemException)?.failure
        val sourceFailure = if (failure == AssetFileSystemFailure.NOT_FOUND) {
            AssetSourceFailure.NOT_FOUND
        } else {
            AssetSourceFailure.READ_FAILED
        }
        failSource(sourceFailure, failure.fileSystemDiagnostic())
    }
}

private class CancellationState(
    var cancelled: Boolean = false,
    var settled: Boolean = false,
    var reject: ((Throwable) -> Unit)? = null,
)

private fun AssetFileSystemFailure?.fileSystemDiagnostic(): String = when (this) {
    AssetFileSystemFailure.NOT_FOUND -> "文件系统资产已删除或不存在"
    AssetFileSystemFailure.PERMISSION_REVOKED -> "文件系统资产权限已撤销"
    AssetFileSystemFailure.CANCELLED -> "文件系统资产读取已取消"
    AssetFileSystemFailure.READ_FAILED,
    null,
    -> "文件系统资产读取失败"
}

private fun requireHttpUri(uri: AssetUri, label: String): AssetUri {
    if (!uri.isNonOpaqueHttp()) failSource("$label 必须是 HTTP(S) 非 opaque URL")
    return uri
}

private fun AssetUri.isNonOpaqueHttp(): Boolean {
    val resolvedProtocol = protocol.ifBlank {
        value.substringBefore(':', missingDelimiterValue = "").takeIf(String::isNotEmpty)?.plus(":").orEmpty()
    }
    return !origin.equals("null", ignoreCase = true) &&
        origin.isNotBlank() &&
        resolvedProtocol.lowercase() in HTTP_PROTOCOLS
}

private val HTTP_PROTOCOLS = setOf("http:", "https:")
private const val HTTP_NOT_FOUND = 404

private fun String.withDirectorySlash(): String = if (endsWith('/')) this else "$this/"

private fun rejectedSourceRead(message: String): AssetSourceReadOperation = AssetSourceReadOperation(
    Promise.reject(AssetPolicyException(message)),
) {}

private fun failSource(message: String): Nothing = throw AssetPolicyException(message)

private fun failSource(failure: AssetSourceFailure, message: String): Nothing =
    throw AssetSourceException(failure, message)
